<script setup lang="ts">
/**
 * Aura Incorporation — "Incorporation Documents" 번들 (신버전 템플릿 기준 10개 섹션).
 *  Welcome Pointers · Corporate Secretarial Service Agreement · Nomination Letter ·
 *  Letter of Indemnity · Checklist for Financial Year · Declaration for Registers ·
 *  Nominee Resident Director Agreement (+ Schedule 1) · Schedule 2 Service Agreement ·
 *  Registered Address Service Agreement + Annexure B.
 *  Firm: AURA PARTNERS (SINGAPORE) PTE. LTD. (201717043E).
 *
 *  서명란 규칙 (템플릿과 1:1)
 *   - 회사측 서명자 이름 = [[director_name]] (기본: 1번 이사, Client Acceptance 에서 자동)
 *   - Aura 측 서명자 이름 = [[aura_signatory_name]] (Managing Director — 수기 입력)
 *   - 노미니 계약 Service Provider(ABELL GROUP) 서명자 = [[serviceprovider_signatory_name]]
 *   - 서명란 날짜 = 이 번들 전용 일자(incdocs_dated_on → 생성 시 [[dated_on]])
 *  Schedule 1(Principal/Director/Shareholder/Authorised Person 목록)은 Client Acceptance 인원으로 자동 반복된다.
 */
import CddVarField from './cdd/CddVarField.vue';
import './allotDoc.css';
</script>

<template>
  <div class="allot-doc">
    <!-- 번들 공통 서명자 — 모든 섹션 서명란에 같은 값이 들어간다 -->
    <p class="doc-sec-title" style="text-align:left">Signatories (used in every signature block of this bundle)</p>
    <table class="cdd-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell">Company — signing Director</td>
          <td><CddVarField field-key="director_name" label="Signing Director Name" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">AuraPartners — Managing Director</td>
          <td><CddVarField field-key="aura_signatory_name" label="AuraPartners Signatory (Managing Director)" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Date (all signature blocks)</td>
          <td><CddVarField field-key="incdocs_dated_on" label="Dated On" /></td>
        </tr>
      </tbody>
    </table>

    <!-- ①  Welcome / Pointers -->
    <div class="doc-lbl-title" style="margin-top:16px">WELCOME — POINTERS FOR OUR WORKING RELATIONSHIP</div>
    <p class="doc-p">Dear Sir/Madam, we are providing you the following pointers for ease of facilitating our working relationship and to ensure that your presence in Singapore is compliant with the relevant regulations.</p>
    <p class="doc-note">Contact table (Name / Contact No. / Email) is filled automatically from the directors entered in the Client Acceptance form.</p>
    <div class="doc-sig-row">
      <div>For AURA PARTNERS (SINGAPORE) PTE. LTD. — Name: <CddVarField field-key="aura_signatory_name" label="AuraPartners Signatory" /> · Managing Director</div>
      <div>Acknowledged by <CddVarField field-key="company_name" label="Company Name" /> — Name: <CddVarField field-key="director_name" label="Signing Director Name" /> · Director · Date: <CddVarField field-key="incdocs_dated_on" label="Dated On" /></div>
    </div>

    <!-- ②  Corporate Secretarial Service Agreement -->
    <div class="doc-lbl-title" style="margin-top:16px">CORPORATE SECRETARIAL SERVICE AGREEMENT</div>
    <p class="doc-p">This AGREEMENT is made this <CddVarField field-key="agreement_date" label="Agreement Date" /> between <CddVarField field-key="company_name" label="Company Name" /> (Company Registration No. <CddVarField field-key="company_registration_no" label="Company Registration No." />), having its registered office at <CddVarField field-key="registered_office_address" label="Registered Office Address" />, and AURA PARTNERS (SINGAPORE) PTE. LTD. (201717043E).</p>

    <p class="doc-sec-title">Annual Fees</p>
    <table class="cdd-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell">Nominee Secretary — Annual Fee</td>
          <td><CddVarField field-key="fee_nominee_secretary_annual" label="Nominee Secretary Annual Fee" /></td>
        </tr>
        <tr>
          <!-- Engagement Letter "Corporate Secretarial Service (per annum)" 와 같은 칸 — 한 곳만 입력 -->
          <td class="cdd-lcell">Corporate Secretarial Services — Annual Fee <span class="doc-note">(shared with Engagement Letter)</span></td>
          <td><CddVarField field-key="fee_annual_secretarial" label="Corporate Secretarial Annual Fee" /></td>
        </tr>
      </tbody>
    </table>

    <p class="doc-sec-title">Service Selection (mark the services to provide)</p>
    <table class="cdd-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell">Standard Annual Secretarial Services</td>
          <td><CddVarField field-key="svc_secretarial" label="Standard Annual Secretarial Services" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Provision of Registered Office</td>
          <td><CddVarField field-key="svc_registered_office" label="Provision of Registered Office" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Provision of Nominee Director</td>
          <td><CddVarField field-key="svc_nominee_director" label="Provision of Nominee Director" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Provision of Nominee Shareholder</td>
          <td><CddVarField field-key="svc_nominee_shareholder" label="Provision of Nominee Shareholder" /></td>
        </tr>
      </tbody>
    </table>
    <div class="doc-sig-row">
      <div>For AURA PARTNERS (SINGAPORE) PTE. LTD. — Name: <CddVarField field-key="aura_signatory_name" label="AuraPartners Signatory" /> · Managing Director · Date: <CddVarField field-key="incdocs_dated_on" label="Dated On" /></div>
      <div>For and on behalf of <CddVarField field-key="company_name" label="Company Name" /> — Name: <CddVarField field-key="director_name" label="Signing Director Name" /> · Director · Date: <CddVarField field-key="incdocs_dated_on" label="Dated On" /></div>
    </div>

    <!-- ③  Nomination Letter -->
    <div class="doc-lbl-title" style="margin-top:16px">NOMINATION LETTER</div>
    <p class="doc-p">Date: <CddVarField field-key="incdocs_dated_on" label="Dated On" /> · <CddVarField field-key="company_name" label="Company Name" /> · <CddVarField field-key="registered_office_address" label="Registered Office Address" /></p>
    <p class="doc-p">The Board of Directors appoint AuraPartners to act as secretarial agent and authorise the lodgement of the Register of Registrable Controllers with ACRA.</p>
    <div class="doc-sig-row">
      <div>Yours faithfully — <CddVarField field-key="director_name" label="Signing Director Name" /> · Director</div>
    </div>

    <!-- ④  Letter of Indemnity -->
    <div class="doc-lbl-title" style="margin-top:16px">LETTER OF INDEMNITY</div>
    <p class="doc-p"><b><CddVarField field-key="company_name" label="Company Name" /> — APPOINTMENT OF COMPANY SECRETARY.</b> In consideration of your acting as the Secretary of the Company, we furnish you with this Letter of Indemnity (continuing and irrevocable until the company is liquidated and dissolved).</p>
    <div class="doc-sig-row">
      <div><b>Confirm by:</b> Name <CddVarField field-key="director_name" label="Signing Director Name" /> · Identification No. <CddVarField field-key="director_identification_no" label="Director Identification No." /> · Date <CddVarField field-key="incdocs_dated_on" label="Dated On" /></div>
      <div><b>Witnessed by:</b> Name <CddVarField field-key="witness_name" label="Witness Name" /> · Identification No. <CddVarField field-key="witness_identification_no" label="Witness Identification No." /> · Date <CddVarField field-key="witness_date" label="Witness Date" /></div>
    </div>

    <!-- ⑤  Financial Year Checklist -->
    <div class="doc-lbl-title" style="margin-top:16px">CHECKLIST FOR FINANCIAL YEAR</div>
    <p class="doc-p">To: <CddVarField field-key="company_name" label="Company Name" /> · From: AURA PARTNERS (SINGAPORE) PTE. LTD.</p>
    <p class="doc-p">Financial Year End: we would like to fix the year-end to be on <CddVarField field-key="financial_year_end" label="Financial Year End" /></p>

    <p class="doc-sec-title">Auditor</p>
    <table class="cdd-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell">Appoint a named auditor</td>
          <td><CddVarField field-key="audit_appoint" label="We are appointing an auditor" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Audit Firm's Name</td>
          <td><CddVarField field-key="audit_firm_name" label="Audit Firm's Name" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Audit Firm's Address</td>
          <td><CddVarField field-key="audit_firm_address" label="Audit Firm's Address" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Or — ask AuraPartners to arrange an auditor</td>
          <td><CddVarField field-key="audit_arrange" label="Arrange an auditor for us" /></td>
        </tr>
      </tbody>
    </table>

    <p class="doc-sec-title">Bookkeeping / Tax</p>
    <table class="cdd-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell">Arrange bookkeeping and tax services</td>
          <td><CddVarField field-key="bookkeeping_arrange" label="Arrange bookkeeping / tax services" /></td>
        </tr>
      </tbody>
    </table>

    <p class="doc-sec-title">Opening of Bank Account</p>
    <table class="cdd-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell">Bank</td>
          <td><CddVarField field-key="bank_name" label="Bank" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Branch</td>
          <td><CddVarField field-key="bank_branch" label="Branch" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Manner of Operation</td>
          <td><CddVarField field-key="bank_operation" label="Manner of Operation" /></td>
        </tr>
      </tbody>
    </table>

    <p class="doc-sec-title">Any Other Matter(s)</p>
    <table class="cdd-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell">GST Application</td>
          <td><CddVarField field-key="apply_gst" label="GST Application" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">CR (Customs &amp; Registration) Application</td>
          <td><CddVarField field-key="apply_cr" label="CR Application" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Licence Application</td>
          <td><CddVarField field-key="apply_license" label="Licence Application" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Licence (please specify)</td>
          <td><CddVarField field-key="license_detail" label="Licence Detail" /></td>
        </tr>
      </tbody>
    </table>
    <div class="doc-sig-row">
      <div>Thank you — Name: <CddVarField field-key="director_name" label="Signing Director Name" /> · <CddVarField field-key="company_name" label="Company Name" /></div>
    </div>

    <!-- ⑥  Declaration for Registers -->
    <div class="doc-lbl-title" style="margin-top:16px">DECLARATION FOR REGISTER OF REGISTRABLE CONTROLLERS, NOMINEE DIRECTORS AND NOMINEE SHAREHOLDERS</div>
    <p class="doc-p">Name of Company: <CddVarField field-key="company_name" label="Company Name" /> · UEN No.: <CddVarField field-key="company_registration_no" label="Company Registration No." /></p>
    <table class="cdd-tbl">
      <thead>
        <tr><th>Register — kept at</th><th>Registered office of the company</th><th>Registered office of a corporate service provider</th></tr>
      </thead>
      <tbody>
        <tr>
          <td class="cdd-lcell">Registrable Controllers</td>
          <td><CddVarField field-key="roc_at_company" label="ROC at Company office" /></td>
          <td><CddVarField field-key="roc_at_agent" label="ROC at corporate service provider" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Nominee Directors</td>
          <td><CddVarField field-key="rond_at_company" label="ROND at Company office" /></td>
          <td><CddVarField field-key="rond_at_agent" label="ROND at corporate service provider" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Nominee Shareholders</td>
          <td><CddVarField field-key="rons_at_company" label="RONS at Company office" /></td>
          <td><CddVarField field-key="rons_at_agent" label="RONS at corporate service provider" /></td>
        </tr>
      </tbody>
    </table>
    <div class="doc-sig-row">
      <div>Dated <CddVarField field-key="incdocs_dated_on" label="Dated On" /> — For and on behalf of <CddVarField field-key="company_name" label="Company Name" />: <CddVarField field-key="director_name" label="Signing Director Name" /> · Director</div>
    </div>

    <!-- ⑦  Nominee Resident Director Agreement -->
    <div class="doc-lbl-title" style="margin-top:16px">NOMINEE RESIDENT DIRECTOR AGREEMENT</div>
    <p class="doc-p">This AGREEMENT is made this <CddVarField field-key="agreement_date" label="Agreement Date" /> between <CddVarField field-key="company_name" label="Company Name" /> (Company Registration No. <CddVarField field-key="company_registration_no" label="Company Registration No." />) ("Company"); <CddVarField field-key="ubo_name" label="Principal (UBO) Name" /> (Passport No. <CddVarField field-key="ubo_identification_no" label="Principal ID" />) of <CddVarField field-key="ubo_residential_address" label="Principal Residential Address" /> ("Principal"); and ABELL GROUP PTE. LTD. ("Service Provider").</p>
    <p class="doc-sec-title">Fees</p>
    <table class="cdd-tbl">
      <tbody>
        <!-- Engagement Letter 와 같은 칸(fee_annual_nominee_director / fee_nominee_director_deposit) -->
        <tr>
          <td class="cdd-lcell">Nominee Director — Annual Fee <span class="doc-note">(shared with Engagement Letter)</span></td>
          <td><CddVarField field-key="fee_annual_nominee_director" label="Nominee Director Annual Fee" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Nominee Director — Security Deposit <span class="doc-note">(shared with Engagement Letter)</span></td>
          <td><CddVarField field-key="fee_nominee_director_deposit" label="Nominee Director Deposit" /></td>
        </tr>
      </tbody>
    </table>
    <p class="doc-sec-title">Execution</p>
    <table class="cdd-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell">The Company — signing Director</td>
          <td><CddVarField field-key="director_name" label="Signing Director Name" /> <span class="doc-note">(same as the Signatories block)</span></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Witness (Company) — Name / ID</td>
          <td><CddVarField field-key="company_witness_name" label="Company Witness Name" /> / <CddVarField field-key="company_witness_identification_no" label="Company Witness ID" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">The Principal</td>
          <td><CddVarField field-key="ubo_name" label="Principal (UBO) Name" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Witness (Principal) — Name / ID</td>
          <td><CddVarField field-key="principal_witness_name" label="Principal Witness Name" /> / <CddVarField field-key="principal_witness_identification_no" label="Principal Witness ID" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">The Service Provider (ABELL GROUP) — signing Director</td>
          <td><CddVarField field-key="serviceprovider_signatory_name" label="Service Provider Signatory" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Witness (Service Provider) — Name / ID</td>
          <td><CddVarField field-key="serviceprovider_witness_name" label="Service Provider Witness Name" /> / <CddVarField field-key="serviceprovider_witness_identification_no" label="Service Provider Witness ID" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Authorised Person</td>
          <td><CddVarField field-key="authorised_person_name" label="Authorised Person Name" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Witness (Authorised Person) — Name / ID</td>
          <td><CddVarField field-key="authorised_person_witness_name" label="Authorised Person Witness Name" /> / <CddVarField field-key="authorised_person_witness_identification_no" label="Authorised Person Witness ID" /></td>
        </tr>
      </tbody>
    </table>
    <p class="doc-note">Schedule 1 (Principal / Director / Shareholder / Authorised Person particulars) is filled automatically from the persons entered in the Client Acceptance form — one row per person.</p>

    <!-- ⑧  Schedule 2 — Service Agreement (compilation & tax) -->
    <div class="doc-lbl-title" style="margin-top:16px">SCHEDULE 2 — SERVICE AGREEMENT (COMPILATION &amp; TAX)</div>
    <p class="doc-p">Fees are fixed in the agreement text. Signed by both parties:</p>
    <div class="doc-sig-row">
      <div>For AURA PARTNERS (SINGAPORE) PTE. LTD. — <CddVarField field-key="aura_signatory_name" label="AuraPartners Signatory" /> · Managing Director · Date: <CddVarField field-key="incdocs_dated_on" label="Dated On" /></div>
      <div>We accept — For and on behalf of <CddVarField field-key="company_name" label="Company Name" />: <CddVarField field-key="director_name" label="Signing Director Name" /> · Director</div>
    </div>

    <!-- ⑨  Registered Address Service + Annexure B -->
    <div class="doc-lbl-title" style="margin-top:16px">REGISTERED ADDRESS SERVICE &amp; ANNEXURE B</div>
    <p class="doc-p">Date: <CddVarField field-key="incdocs_dated_on" label="Dated On" /></p>
    <table class="cdd-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell">Registered Address Service — Annual Fee <span class="doc-note">(shared with Engagement Letter)</span></td>
          <td><CddVarField field-key="fee_annual_registered_address" label="Registered Address Annual Fee" /></td>
        </tr>
      </tbody>
    </table>
    <div class="doc-sig-row">
      <div>For and on behalf of <CddVarField field-key="company_name" label="Company Name" />: <CddVarField field-key="director_name" label="Signing Director Name" /> · Director</div>
    </div>
    <p class="doc-sec-title">Annexure B — Standing Instructions</p>
    <table class="cdd-tbl">
      <tbody>
        <tr>
          <!-- [[office_loc]] — 기본값 = Registered Office Address (직접 수정 가능) -->
          <td class="cdd-lcell">Office Location</td>
          <td><CddVarField field-key="office_loc" label="Office Location" /></td>
        </tr>
      </tbody>
    </table>
    <p class="doc-sec-title">Mail Handling (select one)</p>
    <table class="cdd-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell">Keep for collection</td>
          <td><CddVarField field-key="mail_collection" label="Keep for collection" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Forward to below address</td>
          <td><CddVarField field-key="mail_forward" label="Forward to below address" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Scan &amp; email</td>
          <td><CddVarField field-key="mail_scan" label="Scan & email" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Contact details for mail notification</td>
          <td><CddVarField field-key="mail_contact" label="Mail notification contact" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Correspondence address for mail forwarding</td>
          <td><CddVarField field-key="mail_forward_address" label="Mail forwarding address" /></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.doc-sig-row { display: flex; flex-direction: column; gap: 6px; margin-top: 8px; padding: 8px 10px; background: #faf8ff; border-radius: 6px; }
.doc-note { font-size: 11.5px; color: #666; font-style: italic; margin: 4px 0 2px; }
</style>
